import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../../api/apiClient';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { getTrade, confirmTrade, skipTrade } from './tradesSlice';
import { fetchStrategy } from '../visualStrategies/visualStrategiesSlice';
import { showNotification } from '../notifications/notificationsSlice';
import { getTradingDefaults } from '../../api/account';
import DWLFLoader from '../../components/DWLFLoader';
import SkipTradeDialog from './SkipTradeDialog';
import {
  computeRiskReward,
  computeRiskAmount,
  formatCurrency,
  formatRelativeTime,
  getSkipReasonLabels,
  buildConfirmOverrides,
  CONFIRM_REASONS,
} from './reviewHelpers';

const formatSymbolForRoute = (symbol) => (symbol ? symbol.replace(/\//g, '-') : '');

/**
 * Convert an ISO timestamp to the value expected by an <input type="datetime-local">,
 * i.e. YYYY-MM-DDTHH:mm in the user's local time. Returns '' for nullish input.
 */
const toLocalDateTimeInput = (iso) => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ReviewTradePage = () => {
  const { tradeId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { current: trade } = useSelector((s) => s.trades);

  // The Redux trades slice holds a single `current` trade shared across all
  // trade-detail surfaces. When we navigate from one /trades/:id/review to
  // another, `current` is whatever was loaded last — possibly the previous
  // route's trade — until the new fetch settles. Treat `trade` as ours only
  // when its id matches the URL; everything else (display, override math,
  // confirm/skip targeting) gates on this so we can't render or act on a
  // mismatched trade.
  const tradeMatchesRoute = trade?.tradeId === tradeId;
  const effectiveTrade = tradeMatchesRoute ? trade : null;

  // Local UI state
  const [tradingDefaults, setTradingDefaults] = useState(null);
  const [strategyName, setStrategyName] = useState(null);
  const [overrideEntryPrice, setOverrideEntryPrice] = useState('');
  const [overrideEntryAt, setOverrideEntryAt] = useState('');
  const [overridePositionSize, setOverridePositionSize] = useState('');
  // Track what the entryAt input was SEEDED with so a no-edit confirm can be
  // detected and the field can be omitted from the POST body (the backend
  // then defaults entryAt to "now" with full second precision instead of the
  // minute-truncated datetime-local value we seeded).
  const [overrideEntryAtSeed, setOverrideEntryAtSeed] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [actionError, setActionError] = useState('');
  // Take-reasons (optional, multi-select) — the symmetric twin of skip
  // reasons. Untagged confirms still work; tagged ones feed the
  // Counterfactual / decision-analytics layer.
  const [confirmReasons, setConfirmReasons] = useState([]);
  const [confirmNote, setConfirmNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipDialogError, setSkipDialogError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  // Async race guard: each invocation of loadTrade bumps this token. The
  // settlement handlers only update state when their captured token matches
  // the current one, so an older slow request can't clobber a newer fetch's
  // result.
  const loadTokenRef = useRef(0);

  // Load the trade. We track 404 separately so we can render a tailored
  // "no longer exists" message instead of the generic load error. The slice
  // doesn't expose a getTrade rejection state, so we read the rejection
  // straight from the dispatch result.
  const loadTrade = useCallback(() => {
    loadTokenRef.current += 1;
    const myToken = loadTokenRef.current;
    setLoading(true);
    setNotFound(false);
    setLoadError('');
    return dispatch(getTrade(tradeId)).then((action) => {
      // A newer load is in flight (route changed or user clicked Retry
      // again) — its settlement will set the correct state. Bail.
      if (myToken !== loadTokenRef.current) {
        return;
      }
      setLoading(false);
      if (getTrade.rejected.match(action)) {
        const message = action?.error?.message || 'Could not load trade';
        // axios stringifies HTTP failures as "Request failed with status code 404"
        if (/404/.test(message)) {
          setNotFound(true);
        } else {
          setLoadError(message);
        }
      }
    });
  }, [dispatch, tradeId]);

  useEffect(() => {
    loadTrade();
  }, [loadTrade]);

  // Load trading defaults for the risk context display. Non-blocking — if it
  // fails we just don't show the % of account number.
  useEffect(() => {
    let cancelled = false;
    getTradingDefaults()
      .then((data) => {
        if (!cancelled && data?.tradingDefaults) {
          setTradingDefaults(data.tradingDefaults);
        }
      })
      .catch(() => {
        // Silent — risk context is a nice-to-have, not load-bearing.
      });
    return () => { cancelled = true; };
  }, []);

  // Best-effort strategy name lookup. The deep-link only includes the
  // strategy ID; this turns it into a human-readable label. Gated on
  // effectiveTrade so we don't look up the wrong strategy when `current`
  // is briefly stale for a different route.
  useEffect(() => {
    if (!effectiveTrade?.triggeredByStrategyId) {
      setStrategyName(null);
      return;
    }
    let cancelled = false;
    dispatch(fetchStrategy(effectiveTrade.triggeredByStrategyId)).then((action) => {
      if (cancelled) {
        return;
      }
      const strategy =
        action?.payload?.strategy || action?.payload || null;
      if (strategy?.name) {
        setStrategyName(strategy.name);
      }
    });
    return () => { cancelled = true; };
  }, [dispatch, effectiveTrade?.triggeredByStrategyId]);

  // Seed the override form with the planned values once the trade loads.
  // Gate on effectiveTrade so the form is never populated from a stale
  // (different route) trade in `current`.
  useEffect(() => {
    if (effectiveTrade && effectiveTrade.status === 'planned') {
      setOverrideEntryPrice(
        effectiveTrade.entryPrice !== undefined && effectiveTrade.entryPrice !== null
          ? String(effectiveTrade.entryPrice)
          : ''
      );
      const seededEntryAt =
        toLocalDateTimeInput(effectiveTrade.entryAt) ||
        toLocalDateTimeInput(new Date().toISOString());
      setOverrideEntryAt(seededEntryAt);
      setOverrideEntryAtSeed(seededEntryAt);
      setOverridePositionSize(
        effectiveTrade.positionSize !== undefined && effectiveTrade.positionSize !== null
          ? String(effectiveTrade.positionSize)
          : ''
      );
      // Reset the take-reasons too — they're per-decision, and without this
      // the previous trade's ticked reasons would leak into the next review.
      setConfirmReasons([]);
      setConfirmNote('');
    }
  }, [effectiveTrade]);

  const accountCurrency = tradingDefaults?.accountCurrency || 'USD';

  // Memoised derived values for the plan card. These reference the trade row
  // values (NOT the override inputs) so the plan card stays an immutable
  // snapshot of what the strategy proposed. Gate on effectiveTrade so the
  // plan card doesn't render numbers from a stale (different-route) trade.
  const rrRatio = useMemo(
    () => computeRiskReward(effectiveTrade?.entryPrice, effectiveTrade?.initialStop, effectiveTrade?.initialTakeProfit),
    [effectiveTrade?.entryPrice, effectiveTrade?.initialStop, effectiveTrade?.initialTakeProfit]
  );
  const riskAmount = useMemo(
    () => computeRiskAmount(effectiveTrade?.entryPrice, effectiveTrade?.initialStop, effectiveTrade?.positionSize),
    [effectiveTrade?.entryPrice, effectiveTrade?.initialStop, effectiveTrade?.positionSize]
  );
  // FX rate (accountCcy -> USD) for non-USD accounts. All platform prices
  // are USD-quoted, so showing risk in the ACCOUNT currency (and computing
  // % of a non-USD balance) needs a conversion — previously riskUSD was
  // divided by a GBP balance, overstating risk % by the FX rate. Reuses the
  // position-size tool endpoint's fx block; on failure we fall back to
  // USD-only display and suppress the cross-currency percentage.
  const [fxRate, setFxRate] = useState(null); // accountCcy -> USD
  useEffect(() => {
    if (accountCurrency === 'USD') {
      setFxRate(1);
      return undefined;
    }
    if (!Number.isFinite(effectiveTrade?.entryPrice) || !Number.isFinite(effectiveTrade?.initialStop)) {
      return undefined;
    }
    let cancelled = false;
    apiClient
      .post('/tools/position-size', {
        accountBalance: tradingDefaults?.accountBalance || 1000,
        riskPercent: 1,
        entry: effectiveTrade.entryPrice,
        stopLoss: effectiveTrade.initialStop,
        currency: accountCurrency,
      })
      .then((res) => {
        const rate = res.data?.fx?.rate;
        if (!cancelled && Number.isFinite(rate) && rate > 0) {setFxRate(rate);}
      })
      .catch(() => {
        // USD-only fallback — never block the review on an FX hiccup.
      });
    return () => { cancelled = true; };
  }, [accountCurrency, effectiveTrade?.entryPrice, effectiveTrade?.initialStop, tradingDefaults?.accountBalance]);

  const riskAmountAccountCcy = useMemo(() => (
    Number.isFinite(riskAmount) && Number.isFinite(fxRate) && fxRate > 0
      ? riskAmount / fxRate
      : null
  ), [riskAmount, fxRate]);

  const riskPctOfAccount = useMemo(() => {
    // The balance is denominated in the ACCOUNT currency, so the percentage
    // must use the converted risk — not raw USD risk over a GBP balance.
    if (riskAmountAccountCcy === null || !tradingDefaults?.accountBalance) {
      return null;
    }
    if (tradingDefaults.accountBalance <= 0) {
      return null;
    }
    return (riskAmountAccountCcy / tradingDefaults.accountBalance) * 100;
  }, [riskAmountAccountCcy, tradingDefaults?.accountBalance]);

  const handleConfirm = async () => {
    setActionError('');
    setFieldErrors({});

    // Build the override body via the pure helper so the diff logic is
    // unit-testable. Backend's confirm endpoint falls back to the planned
    // values when a field is omitted, so a no-edit click sends `{}` and the
    // planned numbers + a fresh "now" timestamp land on the row.
    const result = buildConfirmOverrides({
      overrideEntryPrice,
      plannedEntryPrice: effectiveTrade.entryPrice,
      overrideEntryAt,
      seedEntryAt: overrideEntryAtSeed,
      overridePositionSize,
      plannedPositionSize: effectiveTrade.positionSize,
    });
    if (result.error) {
      setFieldErrors({ [result.error.field]: result.error.message });
      return;
    }
    const body = result.body;

    // Attach the (optional) take-reasons. 'other' needs a note — mirror the
    // backend rule client-side so the failure is inline, not a round-trip.
    if (confirmReasons.length) {
      if (confirmReasons.includes('other') && !confirmNote.trim()) {
        setFieldErrors({ confirmNote: 'Describe the reason — "other" requires a note.' });
        return;
      }
      body.confirmReasons = confirmReasons;
    }
    if (confirmNote.trim()) {
      body.confirmNote = confirmNote.trim();
    }

    setSubmitting(true);
    try {
      const action = await dispatch(confirmTrade({ tradeId, body }));
      if (confirmTrade.fulfilled.match(action)) {
        dispatch(
          showNotification({
            message: 'Trade confirmed',
            severity: 'success',
          })
        );
        navigate(`/trades/${tradeId}`);
        return;
      }
      // Rejected — figure out what kind of error so we can recover.
      const payload = action.payload || {};
      if (payload.code === 'INVALID_STATE') {
        dispatch(
          showNotification({
            message: 'Trade state changed — reloading',
            severity: 'warning',
          })
        );
        dispatch(getTrade(tradeId));
        return;
      }
      if (payload.code === 'VALIDATION') {
        // Back-end validation — surface inline.
        setFieldErrors({ entryPrice: payload.message });
        setActionError(payload.message || 'Validation error');
        return;
      }
      setActionError(payload.message || 'Could not confirm trade.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async ({ skipReasons, skipNote }) => {
    setSkipDialogError('');
    setSubmitting(true);
    try {
      const action = await dispatch(
        skipTrade({ tradeId, body: { skipReasons, ...(skipNote ? { skipNote } : {}) } })
      );
      if (skipTrade.fulfilled.match(action)) {
        const friendly = getSkipReasonLabels(skipReasons).join(', ');
        dispatch(
          showNotification({
            message: `Trade skipped — ${friendly}`,
            severity: 'info',
          })
        );
        setSkipDialogOpen(false);
        navigate(`/trades/${tradeId}`);
        return;
      }
      const payload = action.payload || {};
      if (payload.code === 'INVALID_STATE') {
        dispatch(
          showNotification({
            message: 'Trade state changed — reloading',
            severity: 'warning',
          })
        );
        setSkipDialogOpen(false);
        dispatch(getTrade(tradeId));
        return;
      }
      setSkipDialogError(payload.message || 'Could not skip trade.');
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Render -----

  // Show the loader whenever we don't yet have the trade for THIS route in
  // hand. `current` may be a previous route's trade if the user navigated
  // between /trades/:id/review URLs, so checking `!trade` alone isn't
  // enough — gate on tradeMatchesRoute too.
  if (loading && !effectiveTrade) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box p={3} maxWidth={720} mx="auto">
        <Alert severity="warning" sx={{ borderRadius: 0, mb: 2 }}>
          This planned trade no longer exists.
        </Alert>
        <Button component={RouterLink} to="/trades" variant="contained">
          Back to trade journal
        </Button>
      </Box>
    );
  }

  if (loadError && !effectiveTrade) {
    return (
      <Box p={3} maxWidth={720} mx="auto">
        <Alert severity="error" sx={{ borderRadius: 0, mb: 2 }}>
          Couldn't load trade: {loadError}
        </Alert>
        <Button onClick={loadTrade} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

  if (!effectiveTrade) {
    return null;
  }

  // ----- Headers shared across all status branches -----

  const headerSection = (
    <Box display="flex" alignItems="center" mb={3} flexWrap="wrap" gap={1.5}>
      <IconButton
        onClick={() => navigate('/trades')}
        sx={{
          borderRadius: 0,
          border: '1px solid var(--dwlf-ink-10)',
          '&:hover': {
            borderColor: 'var(--dwlf-accent)',
            backgroundColor: 'var(--dwlf-accent-05)',
          },
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      {trade.assetSymbol && (
        <Tooltip title="View market">
          <IconButton
            component={RouterLink}
            to={`/markets/${formatSymbolForRoute(trade.assetSymbol)}`}
            size="small"
            sx={{
              borderRadius: 0,
              border: '1px solid var(--dwlf-accent-30)',
              color: 'var(--dwlf-accent)',
              '&:hover': {
                backgroundColor: 'var(--dwlf-accent-10)',
                borderColor: 'var(--dwlf-accent)',
              },
            }}
          >
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Typography
        sx={{
          fontFamily: '"Space Grotesk", sans-serif',
          fontSize: { xs: '1.5rem', md: '1.75rem' },
          fontWeight: 600,
          letterSpacing: '-0.02em',
        }}
      >
        {trade.assetSymbol}
      </Typography>
      <Chip
        label={(trade.direction || '').toUpperCase()}
        color={trade.direction === 'long' ? 'success' : 'error'}
        size="small"
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.7rem',
          fontWeight: 600,
          borderRadius: 0,
        }}
      />
      <Chip
        label={trade.status.toUpperCase()}
        size="small"
        color={
          trade.status === 'planned'
            ? 'info'
            : trade.status === 'open'
              ? 'warning'
              : trade.status === 'skipped'
                ? 'default'
                : 'success'
        }
        sx={{
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: '0.7rem',
          fontWeight: 600,
          borderRadius: 0,
        }}
      />
      {strategyName ? (
        <Chip
          label={strategyName}
          size="small"
          variant="outlined"
          sx={{ borderRadius: 0 }}
        />
      ) : trade.triggeredByStrategyId ? (
        <Chip
          label={trade.triggeredByStrategyId}
          size="small"
          variant="outlined"
          sx={{ borderRadius: 0, fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.7rem' }}
        />
      ) : null}
      {trade.createdAt && (
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          Fired {formatRelativeTime(trade.createdAt)}
        </Typography>
      )}
    </Box>
  );

  // ----- Branched body by trade status -----

  // a) planned — the decision view
  if (trade.status === 'planned') {
    return (
      <Box p={3} maxWidth={960} mx="auto">
        {headerSection}

        {/* Plan card — read-only snapshot of what the strategy proposed */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Strategy proposal
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              divider={<Divider orientation="vertical" flexItem />}
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Planned entry
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {formatCurrency(trade.entryPrice, 'USD')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Initial stop
                </Typography>
                <Typography variant="h6" fontWeight={700} color="error.main">
                  {formatCurrency(trade.initialStop, 'USD')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Take-profit
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {formatCurrency(trade.initialTakeProfit, 'USD')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Reward / risk
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {rrRatio !== null ? `${rrRatio.toFixed(2)}R` : '—'}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap">
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Position size
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {trade.positionSize ?? 0} shares
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Risk amount
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {riskAmount !== null ? formatCurrency(riskAmount, 'USD') : '—'}
                  {riskAmountAccountCcy !== null && accountCurrency !== 'USD' && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ≈ {formatCurrency(riskAmountAccountCcy, accountCurrency)}
                    </Typography>
                  )}
                  {riskPctOfAccount !== null && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({riskPctOfAccount.toFixed(2)}% of account)
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Timeframe
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {trade.timeframe || '—'}
                </Typography>
              </Box>
            </Stack>

            {trade.reasonText && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Reasoning
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 0,
                    backgroundColor: 'background.surface',
                    borderColor: 'divider',
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: '0.85rem',
                  }}
                >
                  {trade.reasonText}
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Override card — editable, defaults to planned values */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Confirm with actual fill
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Defaults to the planned values above. Edit to match your actual fill.
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Entry price"
                type="number"
                value={overrideEntryPrice}
                onChange={(e) => setOverrideEntryPrice(e.target.value)}
                error={!!fieldErrors.entryPrice}
                helperText={fieldErrors.entryPrice || `Planned: ${formatCurrency(trade.entryPrice, 'USD')}`}
                inputProps={{ step: 'any', min: 0 }}
                fullWidth
              />
              <TextField
                label="Entry time"
                type="datetime-local"
                value={overrideEntryAt}
                onChange={(e) => setOverrideEntryAt(e.target.value)}
                error={!!fieldErrors.entryAt}
                helperText={fieldErrors.entryAt || 'Defaults to now. Use your actual fill timestamp if backfilling.'}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Position size (shares)"
                type="number"
                value={overridePositionSize}
                onChange={(e) => setOverridePositionSize(e.target.value)}
                error={!!fieldErrors.positionSize}
                helperText={fieldErrors.positionSize || `Planned: ${trade.positionSize ?? 0} shares`}
                inputProps={{ step: 1, min: 1 }}
                fullWidth
              />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Why are you taking it?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Optional, but every tagged confirm feeds the Counterfactual scorecard — tick all
              that apply so future-you can see which take-reasons actually pay.
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <FormGroup>
                {CONFIRM_REASONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={confirmReasons.includes(option.value)}
                        onChange={() =>
                          setConfirmReasons((prev) =>
                            prev.includes(option.value)
                              ? prev.filter((v) => v !== option.value)
                              : [...prev, option.value]
                          )
                        }
                        disabled={submitting}
                      />
                    }
                    label={<Typography variant="body2">{option.label}</Typography>}
                  />
                ))}
              </FormGroup>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 1 }}
              label={confirmReasons.includes('other') ? 'Reason (required)' : 'Entry note (optional)'}
              value={confirmNote}
              onChange={(e) => setConfirmNote(e.target.value)}
              error={!!fieldErrors.confirmNote}
              helperText={fieldErrors.confirmNote || 'Context for your future-self review.'}
              disabled={submitting}
            />

            {actionError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 0 }}>
                {actionError}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Action row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircleIcon />}
            onClick={handleConfirm}
            disabled={submitting}
            size="large"
            sx={{ borderRadius: 0, minWidth: 200 }}
          >
            {submitting ? 'Confirming…' : 'Confirm trade'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            onClick={() => {
              setSkipDialogError('');
              setSkipDialogOpen(true);
            }}
            disabled={submitting}
            size="large"
            sx={{ borderRadius: 0, minWidth: 200 }}
          >
            Skip
          </Button>
        </Stack>

        <SkipTradeDialog
          open={skipDialogOpen}
          onClose={() => setSkipDialogOpen(false)}
          onConfirm={handleSkip}
          submitting={submitting}
          errorMessage={skipDialogError}
        />
      </Box>
    );
  }

  // b) open — already confirmed
  if (trade.status === 'open') {
    return (
      <Box p={3} maxWidth={720} mx="auto">
        {headerSection}
        <Alert severity="info" sx={{ borderRadius: 0, mb: 3 }}>
          This trade is already open.
        </Alert>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Entry price
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {formatCurrency(trade.entryPrice, 'USD')}
                </Typography>
              </Box>
              {trade.entryAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Entry time
                  </Typography>
                  <Typography variant="body1">
                    {new Date(trade.entryAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Position size
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {trade.positionSize ?? 0} shares
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Button
          component={RouterLink}
          to={`/trades/${tradeId}`}
          variant="contained"
          color="primary"
          sx={{ borderRadius: 0 }}
        >
          Go to trade detail
        </Button>
      </Box>
    );
  }

  // c) skipped — already passed on
  if (trade.status === 'skipped') {
    return (
      <Box p={3} maxWidth={720} mx="auto">
        {headerSection}
        <Alert severity="info" sx={{ borderRadius: 0, mb: 3 }}>
          This trade was skipped.
        </Alert>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Reason
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {getSkipReasonLabels(trade.skipReasons, trade.skipReason).join(', ') || '—'}
                </Typography>
              </Box>
              {trade.skipNote && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Note
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 0,
                      backgroundColor: 'background.surface',
                      borderColor: 'divider',
                    }}
                  >
                    {trade.skipNote}
                  </Paper>
                </Box>
              )}
              {trade.skippedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Skipped at
                  </Typography>
                  <Typography variant="body1">
                    {new Date(trade.skippedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
        <Button
          component={RouterLink}
          to={`/trades/${tradeId}`}
          variant="contained"
          color="primary"
          sx={{ borderRadius: 0 }}
        >
          Go to trade detail
        </Button>
      </Box>
    );
  }

  // d) closed — same shape as open
  if (trade.status === 'closed') {
    return (
      <Box p={3} maxWidth={720} mx="auto">
        {headerSection}
        <Alert severity="success" sx={{ borderRadius: 0, mb: 3 }}>
          This trade is closed.
        </Alert>
        <Button
          component={RouterLink}
          to={`/trades/${tradeId}`}
          variant="contained"
          color="primary"
          sx={{ borderRadius: 0 }}
        >
          View detail
        </Button>
      </Box>
    );
  }

  // Unknown status — fallthrough safety net so we don't render a blank page.
  return (
    <Box p={3} maxWidth={720} mx="auto">
      {headerSection}
      <Alert severity="warning" sx={{ borderRadius: 0 }}>
        Unrecognised trade status: {trade.status}
      </Alert>
    </Box>
  );
};

export default ReviewTradePage;
